import React from 'react';
import { Product } from '@/types';
import { ProductCard } from './ProductCard';
import { Package, X, Star } from 'lucide-react';

interface ProductSidebarProps {
  products: Product[];
  isVisible: boolean;
  onProductClick?: (product: Product) => void;
  onClose?: () => void;
  title?: string;
  isLoading?: boolean;
}

export const ProductSidebar: React.FC<ProductSidebarProps> = ({
  products,
  isVisible,
  onProductClick,
  onClose,
  title = "Featured Products",
  isLoading = false,
}) => {
  if (!isVisible) return null;

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <Package size={16} className="text-purple-600" />
            </div>
            <h2 className="font-semibold text-gray-900">{title}</h2>
          </div>
          
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Close"
            >
              <X size={16} className="text-gray-500" />
            </button>
          )}
        </div>
        
        {products.length > 0 && (
          <p className="text-sm text-gray-600 mt-1">
            {products.length} product{products.length !== 1 ? 's' : ''} available
          </p>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <LoadingState />
        ) : products.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="p-4 space-y-4">
            {products.map((product, index) => (
              <ProductCard
                key={`sidebar-product-${index}`}
                product={product}
                onClick={onProductClick}
                variant="sidebar"
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {products.length > 0 && (
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">
              Need more options?
            </p>
            <button className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium">
              View All Products
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const LoadingState: React.FC = () => {
  return (
    <div className="p-4 space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-white rounded-lg border border-gray-200 overflow-hidden animate-pulse">
          <div className="aspect-square bg-gray-200 flex items-center justify-center">
            <img 
              src="https://via.placeholder.com/300x200?text=Loading+Products&textColor=999&backgroundColor=f5f5f5" 
              alt="Loading" 
              className="w-full h-full object-cover opacity-50"
            />
          </div>
          <div className="p-3">
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      ))}
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">Loading products...</p>
        <p className="text-xs text-gray-400 mt-1">Please wait</p>
      </div>
    </div>
  );
};

const EmptyState: React.FC = () => {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Package size={24} className="text-gray-400" />
        </div>
        <h3 className="font-medium text-gray-900 mb-2">No Products Yet</h3>
        <p className="text-sm text-gray-500 mb-4">
          Start chatting to see product recommendations based on your needs.
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center space-x-2 text-blue-800">
            <Star size={16} />
            <span className="text-sm font-medium">Tip</span>
          </div>
          <p className="text-sm text-blue-700 mt-1">
            Tell Mary about your occasion, budget, and quantity to get personalized recommendations!
          </p>
        </div>
      </div>
    </div>
  );
};